import { getTenantConfig } from "../../config/tenantConfig";
import httpClient from "./httpClient";

/**
 * Person Service
 * Uses httpClient for automatic 401 handling and token refresh
 */

/**
 * Search persons by attributes using REST API
 * @param {Object} searchParams - Search parameters (gender, party, eyecolor, etc.)
 * @param {string} searchParams.search - Optional text search (searches searchstring field)
 * @param {string} searchParams.gender - Optional gender filter
 * @param {string} searchParams.party - Optional party filter
 * @param {string} searchParams.eyecolor - Optional eye color filter
 * @param {string} searchParams.agegroup - Optional age group filter
 * @param {string} searchParams.height - Optional height filter
 * @param {string} searchParams.build - Optional build filter
 * @param {string} searchParams.haircolor - Optional hair color filter
 * @param {string} searchParams.hairstyle - Optional hair style filter
 * @param {string} searchParams.facialhair - Optional facial hair filter
 * @param {string} searchParams.skintone - Optional skin tone filter
 * @param {string} searchParams.glasses - Optional glasses filter
 * @param {string} searchParams.dress - Optional dress filter
 * @param {string} searchParams.persontype - Optional person type filter
 * @param {string} searchParams.dataset - Optional dataset filter
 * @param {number} searchParams.limit - Optional result limit (default 10000)
 * @returns {Promise<Array>} Array of person objects matching the search criteria
 */
export async function searchPersons(searchParams) {
  try {
    const config = getTenantConfig();
    const queryPersonsApi = config.api.queryPersons;

    if (!queryPersonsApi) {
      console.error("QueryPersons API endpoint not configured");
      throw new Error(
        "QueryPersons API not configured. Run bin/frontend/configure-api-endpoints.sh",
      );
    }

    // Build query params object (axios will handle URLSearchParams)
    const params = {};
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        params[key] = value;
      }
    });

    console.log(`Searching persons with criteria:`, searchParams);

    const data = await httpClient.get(`${queryPersonsApi}/persons/search`, {
      params,
    });
    console.log(
      `Found ${data.count} persons (scanned ${data.scannedCount} items)`,
    );

    // Backend already provides full URLs - no conversion needed
    return data.persons || [];
  } catch (error) {
    console.error("Error searching persons:", error);
    throw error;
  }
}

/**
 * Query persons by an array of person_ids using REST API
 * @param {Array<string>} personIds - Array of person_id values to query
 * @returns {Promise<Array>} Array of person objects with converted image URLs
 */
export async function getPersonsByIds(personIds) {
  if (!personIds || personIds.length === 0) {
    return [];
  }

  try {
    const config = getTenantConfig();
    const getPersonsApi = config.api.getPersons;

    if (!getPersonsApi) {
      console.error("GetPersons API endpoint not configured");
      throw new Error(
        "GetPersons API not configured. Run bin/frontend/configure-api-endpoints.sh",
      );
    }

    const data = await httpClient.get(`${getPersonsApi}/persons`, {
      params: { ids: personIds.join(",") },
    });

    // Backend already provides full URLs - no conversion needed
    const persons = data.persons || [];

    return persons;
  } catch (error) {
    console.error("Error querying persons by IDs:", error);
    throw error;
  }
}

/**
 * Update person characteristics
 * @param {string} personId - Person ID
 * @param {Object} characteristics - Characteristics to update (e.g., {gender: "MALE", agegroup: "30-40"})
 * @returns {Promise<Object>} API response
 */
export async function updatePersonCharacteristics(personId, characteristics) {
  try {
    const config = getTenantConfig();
    const personUpdateApi = config.api.personUpdate;

    if (!personUpdateApi) {
      console.error("PersonUpdate API endpoint not configured");
      throw new Error(
        "PersonUpdate API not configured. Run bin/frontend/configure-api-endpoints.sh",
      );
    }

    const data = await httpClient.put(
      `${personUpdateApi}/persons/${encodeURIComponent(personId)}`,
      characteristics,
    );

    return data;
  } catch (error) {
    console.error("Error updating person characteristics:", error);
    throw error;
  }
}
